import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Radio, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState, type ReactNode } from "react";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import { confirmDialog } from "@/utils/confirmDialog";
import RequiredMark from "@/views/components/RequiredMark";

export interface SearchFoundRow {
    id: number;
    keyword?: string;
    type?: number | string;
    businessId?: string;
    businessIdTwo?: string;
    clickNum?: number;
    isTop?: number | string;
    createdTime?: string;
    editTime?: string;
}

export interface SearchFoundQuery {
    page: number;
    size: number;
}

export interface SearchFoundFormValues {
    keyword: string;
    type: number;
    businessId: string;
    businessIdTwo: string;
}

export interface SearchFoundSelectorOption {
    id: number | string;
    label: string;
}

interface SearchFoundProductTypeSelectorItem {
    id: number | string;
    name?: string;
    area?: string;
}

interface SearchFoundRouteSelectorItem {
    id: number | string;
    source?: string;
    destination?: string;
    sourceFullName?: string;
    destinationFullName?: string;
}

interface SearchFoundAdPageSelectorItem {
    advert?: {
        id?: number | string;
        title?: string;
    };
}

interface SearchFoundInformationSelectorItem {
    id: number | string;
    title?: string;
}

export interface SearchFoundPageProps {
    loadSearches?: (query: SearchFoundQuery) => Promise<{ list?: SearchFoundRow[]; total?: number }>;
    loadDetail?: (id: number) => Promise<SearchFoundFormValues & { id: number }>;
    create?: (values: SearchFoundFormValues) => Promise<unknown>;
    update?: (id: number, values: SearchFoundFormValues) => Promise<unknown>;
    remove?: (id: number) => Promise<unknown>;
    top?: (id: number) => Promise<unknown>;
    loadProductOptions?: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadProductTypeOptions?: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadRouteOptions?: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadMiniProgramAdOptions?: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadPcAdOptions?: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadInformationOptions?: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    permissions?: string[];
}

// 对齐旧 Vue ProTable 默认每页十条的分页参数。
const defaultQuery: SearchFoundQuery = { page: 1, size: 10 };
const typeLabels: Record<number, string> = {
    1: "搜索词",
    2: "商品",
    3: "砂价",
    4: "运价",
    5: "广告页",
    6: "资讯"
};
const typeOptions = Object.entries(typeLabels).map(([value, label]) => ({ value: Number(value), label }));
const deleteConfirmOptions = {
    closeOnClickModal: false,
    showClose: true,
    confirmButtonText: "确认"
};

/** 复用 Vue 搜索发现分页接口。 */
export async function loadSearchFound(query: SearchFoundQuery): Promise<{ list?: SearchFoundRow[]; total?: number }> {
    const response = await request.service.get("/admin/search/paging", { params: query });
    return response.data;
}

/** 复用 Vue 搜索发现详情接口。 */
export async function loadSearchFoundDetail(id: number): Promise<SearchFoundFormValues & { id: number }> {
    const response = await request.service.get(`/admin/search/${id}`);
    return response.data;
}

/** 复用 Vue 搜索发现新增接口。 */
export async function createSearchFound(values: SearchFoundFormValues): Promise<unknown> {
    const response = await request.service.post("/admin/search", values);
    return response.data;
}

/** 复用 Vue 搜索发现编辑接口。 */
export async function updateSearchFound(id: number, values: SearchFoundFormValues): Promise<unknown> {
    const response = await request.service.put(`/admin/search/${id}`, values);
    return response.data;
}

/** 复用 Vue 搜索发现删除接口。 */
export async function deleteSearchFound(id: number): Promise<unknown> {
    const response = await request.service.delete(`/admin/search/${id}`);
    return response.data;
}

/** 复用 Vue 搜索发现置顶/取消置顶接口。 */
export async function topSearchFound(id: number): Promise<unknown> {
    const response = await request.service.put(`/admin/search/top/${id}`);
    return response.data;
}

interface SearchFoundProductSelectorItem {
    id: number | string;
    title?: string;
    productTypeName?: string;
    productTypeSpec?: string | number;
}

/** 复用 Vue 商品远程选择接口，并保持页码、条数及展示文案契约。 */
export async function loadSearchFoundProductOptions(keyword: string): Promise<SearchFoundSelectorOption[]> {
    const response = await request.service.get<{ list?: SearchFoundProductSelectorItem[] }>(
        "/admin/product-consignment/paging-selector",
        { params: { keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [item.title, item.productTypeName && `${item.productTypeName}(${item.productTypeSpec ?? ""})`, item.id]
            .filter(Boolean)
            .join(" | ")
    }));
}

/** 复用 Vue 砂石指数 remote-method 的分页接口。 */
export async function loadSearchFoundProductTypeOptions(keyword: string): Promise<SearchFoundSelectorOption[]> {
    const response = await request.service.get<{ list?: SearchFoundProductTypeSelectorItem[] }>(
        "/dataService/admin/product-type/paging-selector",
        { params: { keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [item.name, item.area].filter(Boolean).join(" | ")
    }));
}

/** 复用 Vue 运价指数 remote-method 的航线选择接口。 */
export async function loadSearchFoundRouteOptions(keyword: string): Promise<SearchFoundSelectorOption[]> {
    const response = await request.service.get<{ list?: SearchFoundRouteSelectorItem[] }>(
        "/dataService/admin/ship/route/paging-selector",
        { params: { shipRouteName: keyword, page: 1, size: 50 } }
    );
    return (response.data.list || []).map(item => ({
        id: item.id,
        label: [
            item.source && item.destination ? `${item.source}-${item.destination}` : undefined,
            item.sourceFullName && item.destinationFullName
                ? `${item.sourceFullName}-${item.destinationFullName}`
                : undefined
        ]
            .filter(Boolean)
            .join(" | ")
    }));
}

/** 复用 Vue 广告页 remote-method，classify=2 为小程序、classify=1 为 PC。 */
export async function loadSearchFoundAdPageOptions(
    keyword: string,
    classify: 1 | 2
): Promise<SearchFoundSelectorOption[]> {
    const response = await request.service.get<{ list?: SearchFoundAdPageSelectorItem[] }>(
        "/dataService/admin/advert/paging",
        { params: { key: keyword, classify, page: 1, size: 50 } }
    );
    return (response.data.list || [])
        .filter(item => item.advert?.id != null)
        .map(item => ({ id: item.advert!.id!, label: `${item.advert?.title || ""} | ${item.advert!.id}` }));
}

/** 固定广告页终端参数，供页面默认 loader 复用，避免每次渲染创建新函数。 */
export const loadSearchFoundMiniProgramAdOptions = (keyword: string) => loadSearchFoundAdPageOptions(keyword, 2);
export const loadSearchFoundPcAdOptions = (keyword: string) => loadSearchFoundAdPageOptions(keyword, 1);

/** 复用 Vue 资讯 remote-method 的标题选择接口。 */
export async function loadSearchFoundInformationOptions(keyword: string): Promise<SearchFoundSelectorOption[]> {
    const response = await request.service.get<SearchFoundInformationSelectorItem[]>(
        `/dataService/admin/information/selector?title=${encodeURIComponent(keyword)}`
    );
    return (response.data || []).map(item => ({ id: item.id, label: item.title || String(item.id) }));
}

function formatDate(value?: string): string {
    return value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE;
}

function emptyForm(): SearchFoundFormValues {
    return { keyword: "", type: 1, businessId: "", businessIdTwo: "" };
}

function SearchFoundEditor({
    id,
    open,
    loadDetail,
    create,
    update,
    loadProductOptions,
    loadProductTypeOptions,
    loadRouteOptions,
    loadMiniProgramAdOptions,
    loadPcAdOptions,
    loadInformationOptions,
    onClose,
    onSaved
}: {
    id?: number;
    open: boolean;
    loadDetail: (id: number) => Promise<SearchFoundFormValues & { id: number }>;
    create: (values: SearchFoundFormValues) => Promise<unknown>;
    update: (id: number, values: SearchFoundFormValues) => Promise<unknown>;
    loadProductOptions: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadProductTypeOptions: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadRouteOptions: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadMiniProgramAdOptions: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadPcAdOptions: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    loadInformationOptions: (keyword: string) => Promise<SearchFoundSelectorOption[]>;
    onClose: () => void;
    onSaved: () => void;
}) {
    const [form, setForm] = useState<SearchFoundFormValues>(emptyForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [productOptions, setProductOptions] = useState<SearchFoundSelectorOption[]>([]);
    const [productTypeOptions, setProductTypeOptions] = useState<SearchFoundSelectorOption[]>([]);
    const [routeOptions, setRouteOptions] = useState<SearchFoundSelectorOption[]>([]);
    const [miniProgramAdOptions, setMiniProgramAdOptions] = useState<SearchFoundSelectorOption[]>([]);
    const [pcAdOptions, setPcAdOptions] = useState<SearchFoundSelectorOption[]>([]);
    const [informationOptions, setInformationOptions] = useState<SearchFoundSelectorOption[]>([]);

    useEffect(() => {
        if (!open) return;
        setErrors({});
        setErrorMessage(null);
        setProductOptions([]);
        setProductTypeOptions([]);
        setRouteOptions([]);
        setMiniProgramAdOptions([]);
        setPcAdOptions([]);
        setInformationOptions([]);
        if (id == null) {
            setForm(emptyForm());
            return;
        }
        setLoading(true);
        void loadDetail(id)
            .then(detail => {
                setForm({
                    keyword: detail.keyword || "",
                    type: Number(detail.type),
                    businessId: detail.businessId || "",
                    businessIdTwo: detail.businessIdTwo || ""
                });
                // Vue 编辑态会按已有商品 ID 回填候选，保证选择器展示可读名称。
                if (Number(detail.type) === 2 && detail.businessId) {
                    void loadProductOptions(detail.businessId)
                        .then(options =>
                            setProductOptions(
                                options.length ? options : [{ id: detail.businessId, label: detail.businessId }]
                            )
                        )
                        .catch(() => setErrorMessage("商品搜索失败"));
                }
                // 编辑态预加载当前值，确保远程选择器保留旧 Vue 的可读回显。
                if (Number(detail.type) === 3 && detail.businessId) {
                    setProductTypeOptions([{ id: detail.businessId, label: detail.businessId }]);
                    void loadProductTypeOptions(detail.businessId)
                        .then(options =>
                            setProductTypeOptions(
                                options.length ? options : [{ id: detail.businessId, label: detail.businessId }]
                            )
                        )
                        .catch(() => setErrorMessage("砂石指数搜索失败"));
                }
                if (Number(detail.type) === 4 && detail.businessId) {
                    setRouteOptions([{ id: detail.businessId, label: detail.businessId }]);
                    void loadRouteOptions(detail.businessId)
                        .then(options =>
                            setRouteOptions(
                                options.length ? options : [{ id: detail.businessId, label: detail.businessId }]
                            )
                        )
                        .catch(() => setErrorMessage("运价指数搜索失败"));
                }
                if (Number(detail.type) === 5) {
                    if (detail.businessId) {
                        setMiniProgramAdOptions([{ id: detail.businessId, label: detail.businessId }]);
                        void loadMiniProgramAdOptions(detail.businessId)
                            .then(options =>
                                setMiniProgramAdOptions(
                                    options.length ? options : [{ id: detail.businessId, label: detail.businessId }]
                                )
                            )
                            .catch(() => setErrorMessage("小程序广告页搜索失败"));
                    }
                    if (detail.businessIdTwo) {
                        setPcAdOptions([{ id: detail.businessIdTwo, label: detail.businessIdTwo }]);
                        void loadPcAdOptions(detail.businessIdTwo)
                            .then(options =>
                                setPcAdOptions(
                                    options.length
                                        ? options
                                        : [{ id: detail.businessIdTwo, label: detail.businessIdTwo }]
                                )
                            )
                            .catch(() => setErrorMessage("用户PC广告页搜索失败"));
                    }
                }
                if (Number(detail.type) === 6 && detail.businessId) {
                    setInformationOptions([{ id: detail.businessId, label: detail.businessId }]);
                    void loadInformationOptions(detail.businessId)
                        .then(options =>
                            setInformationOptions(
                                options.length ? options : [{ id: detail.businessId, label: detail.businessId }]
                            )
                        )
                        .catch(() => setErrorMessage("资讯搜索失败"));
                }
            })
            .catch(() => setErrorMessage("搜索发现详情加载失败"))
            .finally(() => setLoading(false));
    }, [
        id,
        loadDetail,
        loadInformationOptions,
        loadMiniProgramAdOptions,
        loadPcAdOptions,
        loadProductOptions,
        loadProductTypeOptions,
        loadRouteOptions,
        open
    ]);

    const updateForm = <K extends keyof SearchFoundFormValues>(key: K, value: SearchFoundFormValues[K]) => {
        setForm(current => ({ ...current, [key]: value }));
        setErrors(current => ({ ...current, [String(key)]: "" }));
    };

    const validate = () => {
        const nextErrors: Record<string, string> = {};
        if (!form.keyword.trim()) nextErrors.keyword = "请输入关键词名称";
        if (!form.type) nextErrors.type = "请选择跳转类型";
        const businessMessages: Record<number, string> = {
            2: "请搜索商品名称或商品id选择",
            3: "请搜索品类名称或品类代码选择",
            4: "请搜索码头名称或航线代码选择",
            6: "请搜索资讯标题"
        };
        if (form.type >= 2 && form.type <= 6 && form.type !== 5 && !form.businessId) {
            nextErrors.businessId = businessMessages[form.type] || "请输入业务值";
        }
        if (form.type === 5) {
            if (!form.businessId) nextErrors.businessId = "请搜索小程序广告页标题或编号选择";
            if (!form.businessIdTwo) nextErrors.businessIdTwo = "请搜索用户PC广告页标题或编号选择";
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    /** 按 Vue remote-method 查询商品候选，清空关键词时不发送请求。 */
    const searchProducts = async (keyword: string) => {
        if (!keyword.trim()) {
            setProductOptions([]);
            return;
        }
        try {
            setProductOptions(await loadProductOptions(keyword));
        } catch {
            setProductOptions([]);
            setErrorMessage("商品搜索失败");
        }
    };

    /** 按 Vue remote-method 查询砂石指数候选。 */
    const searchProductTypes = async (keyword: string) => {
        if (!keyword.trim()) {
            setProductTypeOptions([]);
            return;
        }
        try {
            setProductTypeOptions(await loadProductTypeOptions(keyword));
        } catch {
            setProductTypeOptions([]);
            setErrorMessage("砂石指数搜索失败");
        }
    };

    /** 按 Vue remote-method 查询运价航线候选。 */
    const searchRoutes = async (keyword: string) => {
        if (!keyword.trim()) {
            setRouteOptions([]);
            return;
        }
        try {
            setRouteOptions(await loadRouteOptions(keyword));
        } catch {
            setRouteOptions([]);
            setErrorMessage("运价指数搜索失败");
        }
    };

    /** 按 Vue remote-method 查询小程序广告页候选。 */
    const searchMiniProgramAds = async (keyword: string) => {
        if (!keyword.trim()) {
            setMiniProgramAdOptions([]);
            return;
        }
        try {
            setMiniProgramAdOptions(await loadMiniProgramAdOptions(keyword));
        } catch {
            setMiniProgramAdOptions([]);
            setErrorMessage("小程序广告页搜索失败");
        }
    };

    /** 按 Vue remote-method 查询用户 PC 广告页候选。 */
    const searchPcAds = async (keyword: string) => {
        if (!keyword.trim()) {
            setPcAdOptions([]);
            return;
        }
        try {
            setPcAdOptions(await loadPcAdOptions(keyword));
        } catch {
            setPcAdOptions([]);
            setErrorMessage("用户PC广告页搜索失败");
        }
    };

    /** 按 Vue remote-method 查询资讯候选。 */
    const searchInformation = async (keyword: string) => {
        if (!keyword.trim()) {
            setInformationOptions([]);
            return;
        }
        try {
            setInformationOptions(await loadInformationOptions(keyword));
        } catch {
            setInformationOptions([]);
            setErrorMessage("资讯搜索失败");
        }
    };

    /** 切换跳转类型时清空旧类型候选，避免下拉层展示上一次类型的数据。 */
    const changeType = (type: number) => {
        updateForm("type", type);
        updateForm("businessId", "");
        updateForm("businessIdTwo", "");
        setProductOptions([]);
        setProductTypeOptions([]);
        setRouteOptions([]);
        setMiniProgramAdOptions([]);
        setPcAdOptions([]);
        setInformationOptions([]);
    };

    const submit = async () => {
        if (!validate()) return;
        try {
            if (id == null) await create(form);
            else await update(id, form);
            onSaved();
            onClose();
        } catch {
            setErrorMessage("搜索发现保存失败");
        }
    };

    // 每个字段复用旧 Vue 表单的标签列与控件列，校验提示放在控件列下方。
    const renderFormRow = (label: ReactNode, control: ReactNode, controlId?: string, error?: string) => (
        <div className="search-found-editor-form__row grid grid-cols-[95px_minmax(0,1fr)] items-start gap-5">
            {controlId ? (
                <label className="search-found-editor-form__label pt-1 text-right" htmlFor={controlId}>
                    {label}
                </label>
            ) : (
                <div className="search-found-editor-form__label pt-1 text-right">{label}</div>
            )}
            <div className="search-found-editor-form__control min-w-0">
                {control}
                {error ? (
                    <span className="mt-1 block text-red-500" role="alert">
                        {error}
                    </span>
                ) : null}
            </div>
        </div>
    );

    const renderBusinessField = () => {
        if (form.type === 1) return null;
        if (form.type === 5) {
            return (
                <>
                    {renderFormRow(
                        <>
                            <RequiredMark />
                            小程序广告页
                        </>,
                        <Select
                            allowClear
                            aria-label="小程序广告页"
                            id="search-found-business"
                            className="w-full"
                            filterOption={false}
                            options={miniProgramAdOptions.map(option => ({
                                label: option.label,
                                value: String(option.id)
                            }))}
                            placeholder="请搜索小程序广告页标题或编号选择"
                            showSearch
                            value={form.businessId || undefined}
                            onChange={value => updateForm("businessId", String(value || ""))}
                            onSearch={value => void searchMiniProgramAds(value)}
                        />,
                        "search-found-business",
                        errors.businessId
                    )}
                    {renderFormRow(
                        <>
                            <RequiredMark />
                            用户PC广告页
                        </>,
                        <Select
                            allowClear
                            aria-label="用户PC广告页"
                            id="search-found-business-two"
                            className="w-full"
                            filterOption={false}
                            options={pcAdOptions.map(option => ({ label: option.label, value: String(option.id) }))}
                            placeholder="请搜索用户PC广告页标题或编号选择"
                            showSearch
                            value={form.businessIdTwo || undefined}
                            onChange={value => updateForm("businessIdTwo", String(value || ""))}
                            onSearch={value => void searchPcAds(value)}
                        />,
                        "search-found-business-two",
                        errors.businessIdTwo
                    )}
                </>
            );
        }
        const labels: Record<number, string> = { 2: "商品", 3: "砂石指数", 4: "运价指数", 6: "资讯" };
        if (form.type === 2) {
            return renderFormRow(
                <>
                    <RequiredMark />
                    商品
                </>,
                <Select
                    allowClear
                    aria-label="商品搜索"
                    id="search-found-business"
                    className="w-full"
                    filterOption={false}
                    options={productOptions.map(option => ({ label: option.label, value: String(option.id) }))}
                    placeholder="请搜索商品名称或商品id选择"
                    showSearch
                    value={form.businessId || undefined}
                    onChange={value => updateForm("businessId", String(value))}
                    onClear={() => updateForm("businessId", "")}
                    onSearch={value => void searchProducts(value)}
                />,
                "search-found-business",
                errors.businessId
            );
        }
        const placeholders: Record<number, string> = {
            3: "请搜索品类名称或品类代码选择",
            4: "请搜索码头名称或航线代码选择",
            6: "请搜索资讯标题"
        };
        const optionsByType: Record<number, SearchFoundSelectorOption[]> = {
            3: productTypeOptions,
            4: routeOptions,
            6: informationOptions
        };
        const searchByType: Record<number, (keyword: string) => void> = {
            3: keyword => void searchProductTypes(keyword),
            4: keyword => void searchRoutes(keyword),
            6: keyword => void searchInformation(keyword)
        };
        return renderFormRow(
            <>
                <RequiredMark />
                {labels[form.type] || "业务值"}
            </>,
            <Select
                allowClear
                aria-label={labels[form.type] || "业务值"}
                id="search-found-business"
                className="w-full"
                filterOption={false}
                options={(optionsByType[form.type] || []).map(option => ({
                    label: option.label,
                    value: String(option.id)
                }))}
                placeholder={placeholders[form.type]}
                showSearch
                value={form.businessId || undefined}
                onChange={value => updateForm("businessId", String(value || ""))}
                onSearch={searchByType[form.type]}
            />,
            "search-found-business",
            errors.businessId
        );
    };

    const dialogFooter = (
        <Space size={8}>
            <Button className="search-found-editor-dialog__button" onClick={onClose}>
                取消
            </Button>
            <Button
                aria-label="保存搜索发现"
                className="search-found-editor-dialog__button"
                type="primary"
                onClick={() => void submit()}
            >
                保存
            </Button>
        </Space>
    );

    return (
        <ZhDialog
            alignCenter
            className="search-found-editor-dialog"
            destroyOnClose
            footer={dialogFooter}
            open={open}
            title={id == null ? "新增关键词" : "编辑关键词"}
            width={650}
            onCancel={onClose}
        >
            {loading ? <div>加载中</div> : null}
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            <div className="search-found-editor-form grid gap-4">
                {renderFormRow(
                    <>
                        <RequiredMark />
                        关键词名称
                    </>,
                    <Input
                        aria-label="关键词名称"
                        id="search-found-keyword"
                        className="w-full"
                        maxLength={10}
                        showCount
                        value={form.keyword}
                        onChange={event => updateForm("keyword", event.target.value)}
                    />,
                    "search-found-keyword",
                    errors.keyword
                )}
                {renderFormRow(
                    <>
                        <RequiredMark />
                        跳转类型
                    </>,
                    <Radio.Group
                        aria-label="跳转类型"
                        options={typeOptions}
                        value={form.type}
                        onChange={event => {
                            changeType(Number(event.target.value));
                        }}
                    />,
                    undefined,
                    errors.type
                )}
                {renderBusinessField()}
                <div aria-hidden="true" className="search-found-editor-form__footer-spacer" />
            </div>
        </ZhDialog>
    );
}

/** 搜索发现配置列表与编辑弹窗，保留 Vue 的置顶状态机和类型分支。 */
export function SearchFoundPage({
    loadSearches = loadSearchFound,
    loadDetail = loadSearchFoundDetail,
    create = createSearchFound,
    update = updateSearchFound,
    remove = deleteSearchFound,
    top = topSearchFound,
    loadProductOptions = loadSearchFoundProductOptions,
    loadProductTypeOptions = loadSearchFoundProductTypeOptions,
    loadRouteOptions = loadSearchFoundRouteOptions,
    loadMiniProgramAdOptions = loadSearchFoundMiniProgramAdOptions,
    loadPcAdOptions = loadSearchFoundPcAdOptions,
    loadInformationOptions = loadSearchFoundInformationOptions,
    permissions
}: SearchFoundPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const canManage = currentPermissions.includes("ROLE_SEARCH_W");
    const [rows, setRows] = useState<SearchFoundRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState(defaultQuery);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);
    const [editor, setEditor] = useState<{ open: boolean; id?: number }>({ open: false });

    useEffect(() => {
        let disposed = false;
        void loadSearches(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("搜索发现列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadSearches, query, reloadToken]);

    const runAction = async (action: () => Promise<unknown>) => {
        try {
            await action();
            setReloadToken(value => value + 1);
        } catch {
            setErrorMessage("搜索发现操作失败");
        }
    };

    /** 对齐 Vue confirmable：删除请求必须在用户确认后发送，取消不提示操作失败。 */
    const confirmDelete = async (id: number) => {
        try {
            const confirmed = await confirmDialog("是否删除", "提示", deleteConfirmOptions);
            if (!confirmed) return;
            await runAction(() => remove(id));
        } catch {
            // MessageBox 取消/关闭时沿用 Vue 页面静默返回，避免误报操作失败。
        }
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 搜索发现列表的状态与操作插槽。
    const columns: TableColumnProps<SearchFoundRow>[] = [
        { key: "id", label: "编号", width: 120, prop: "id", render: ({ row }) => row.id },
        {
            key: "keyword",
            label: "关键词",
            width: 157,
            prop: "keyword",
            render: ({ row }) => row.keyword || DEFAULT_VALUE
        },
        {
            key: "type",
            label: "跳转类型",
            width: 157,
            prop: "type",
            render: ({ row }) => typeLabels[Number(row.type)] || DEFAULT_VALUE
        },
        {
            key: "clickNum",
            label: "点击量",
            width: 157,
            prop: "clickNum",
            render: ({ row }) => row.clickNum ?? DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            width: 157,
            prop: "createdTime",
            render: ({ row }) => formatDate(row.createdTime)
        },
        {
            key: "editTime",
            label: "更新时间",
            width: 157,
            prop: "editTime",
            render: ({ row }) => formatDate(row.editTime)
        },
        {
            key: "isTop",
            label: "是否置顶",
            width: 157,
            prop: "isTop",
            render: ({ row }) => (Number(row.isTop) === 1 ? "是" : "否")
        },
        {
            key: "operation",
            label: "操作",
            className: "admin-table-operation-column compact",
            fixed: "right",
            width: 177,
            prop: "id",
            render: ({ row }) =>
                !canManage ? null : Number(row.isTop) === 1 ? (
                    <Button
                        aria-label={`取消置顶搜索发现 ${row.id}`}
                        disabled={banButton}
                        type="link"
                        onClick={() => void runAction(() => top(row.id))}
                    >
                        取消置顶
                    </Button>
                ) : (
                    <Space>
                        <Button
                            aria-label={`编辑搜索发现 ${row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => setEditor({ open: true, id: row.id })}
                        >
                            编辑
                        </Button>
                        <Button
                            aria-label={`删除搜索发现 ${row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void confirmDelete(row.id)}
                        >
                            删除
                        </Button>
                        <Button
                            aria-label={`置顶搜索发现 ${row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => void runAction(() => top(row.id))}
                        >
                            置顶
                        </Button>
                    </Space>
                )
        }
    ];

    return (
        <section aria-label="search-found-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<SearchFoundRow>
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    rowKey={row => String(row.id)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                搜索发现配置
                            </Typography.Title>
                            <div className="admin-table-filter-form mb-4">
                                {canManage ? (
                                    <Button
                                        className="admin-table-primary-action"
                                        aria-label="新增搜索发现"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => setEditor({ open: true })}
                                    >
                                        新增
                                    </Button>
                                ) : null}
                            </div>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                alwaysShowQuickJumper
                                hideOnSinglePage
                                locale={{ jump_to: "转到" }}
                                onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                            />
                        </div>
                    }
                />
            </Card>
            <SearchFoundEditor
                create={create}
                id={editor.id}
                loadDetail={loadDetail}
                loadInformationOptions={loadInformationOptions}
                loadMiniProgramAdOptions={loadMiniProgramAdOptions}
                loadPcAdOptions={loadPcAdOptions}
                loadProductOptions={loadProductOptions}
                loadProductTypeOptions={loadProductTypeOptions}
                loadRouteOptions={loadRouteOptions}
                open={editor.open}
                update={update}
                onClose={() => setEditor({ open: false })}
                onSaved={() => setReloadToken(value => value + 1)}
            />
        </section>
    );
}
